<template>
  <div id="billingContainer" class="page-container">
    <div class="page-content">
      <header class="billing-page-heading flex-row-center mt-16">
        <img
          src="../assets/images/navbar/ic_billing.webp"
          class="icon-pages"
          alt=""
        />
        <span class="page-title">{{ $t("page.billing") }}</span>
        <span class="page-des ml-16 billing-page-description">
          {{ $t("page.billing_description") }}
        </span>
      </header>

      <main class="billing-workspace">
        <div
          v-if="billingLoadError"
          class="billing-alert"
          role="alert"
        >
          <div>
            <strong>{{ $t("billing.unable_to_load") }}</strong>
            <p>{{ billingLoadError }}</p>
          </div>
          <el-button
            type="text"
            size="small"
            class="billing-action-link"
            @click="loadBillingPage"
          >
            {{ $t("billing.try_again") }}
          </el-button>
        </div>

        <section class="period-panel" aria-labelledby="billing-period-heading">
          <div class="period-panel-copy">
            <div class="billing-eyebrow">{{ $t("billing.account_billing") }}</div>
            <div class="period-title-row">
              <h2 id="billing-period-heading">{{ periodLabel }}</h2>
              <span class="period-status" :class="'period-status--' + periodStatus.tone">
                <span class="period-status-dot" aria-hidden="true"></span>
                {{ periodStatus.label }}
              </span>
            </div>
            <p class="period-range">
              {{ billingPeriodDescription }}
            </p>
            <p class="period-updated" aria-live="polite">
              {{ lastUpdatedLabel }}
            </p>
          </div>

          <div class="period-actions">
            <label class="period-select-label" for="billing-period-select">
              {{ $t("billing.billing_period") }}
            </label>
            <el-select
              id="billing-period-select"
              :value="selectedMonthValue"
              class="period-select"
              :disabled="loadingDatableTable"
              @change="monthBilling"
            >
              <el-option
                v-for="period in MonthOfYearOpt"
                :key="period.value"
                :label="period.label"
                :value="period.value"
              />
            </el-select>
            <el-button
              type="text"
              class="header-icon-btn billing-export-button"
              icon="el-icon-download"
              :disabled="loadingDatableTable || !htmlExport"
              @click="exportHtml"
            >
              {{ $t("billing.export_summary") }}
            </el-button>
          </div>
        </section>

        <div class="calculation-note">
          <i class="el-icon-info" aria-hidden="true"></i>
          <span>{{ $t("billing.live_calculation_note") }}</span>
        </div>

        <section class="billing-section" aria-labelledby="charges-heading">
          <div class="section-heading">
            <div>
              <div class="billing-eyebrow">{{ $t("billing.period_overview") }}</div>
              <h2 id="charges-heading">{{ $t("billing.how_charges_add_up") }}</h2>
            </div>
            <p>{{ $t("billing.pending_separate") }}</p>
          </div>

          <div v-if="loadingDatableTable && !billingSummary.length" class="summary-skeleton">
            <div v-for="item in 3" :key="'summary-skeleton-' + item" class="skeleton-block"></div>
          </div>

          <div v-else-if="billingSummary.length" class="currency-summary-list">
            <article
              v-for="item in billingSummary"
              :key="item.currency"
              class="currency-summary"
            >
              <div class="currency-summary-header">
                <div>
                  <span class="currency-code">{{ item.currency }}</span>
                  <span class="currency-summary-label">{{ $t("billing.recorded_charges") }}</span>
                </div>
                <div class="currency-total">
                  {{ formatMoney(summaryTotal(item), item.currency) }}
                </div>
              </div>

              <div class="billing-equation" :aria-label="$t('billing.charge_calculation')">
                <div class="equation-item">
                  <span>{{ $t("billing.subscription_label") }}</span>
                  <strong>{{ formatMoney(item.subscription, item.currency) }}</strong>
                </div>
                <span class="equation-operator" aria-hidden="true">+</span>
                <div class="equation-item">
                  <span>{{ $t("billing.rewards_charged") }}</span>
                  <strong>{{ formatMoney(item.reward_charges, item.currency) }}</strong>
                </div>
                <span class="equation-operator" aria-hidden="true">+</span>
                <div class="equation-item">
                  <span>{{ $t("billing.credit_top_ups") }}</span>
                  <strong>{{ formatMoney(item.topupCredit, item.currency) }}</strong>
                </div>
                <span class="equation-operator equation-operator--equals" aria-hidden="true">=</span>
                <div class="equation-item equation-item--total">
                  <span>{{ $t("billing.recorded_total") }}</span>
                  <strong>{{ formatMoney(summaryTotal(item), item.currency) }}</strong>
                </div>
              </div>

              <div class="wallet-strip">
                <div class="wallet-metric">
                  <span>{{ $t("billing.available_reward_credit") }}</span>
                  <strong>{{ formatMoney(item.reward_credit_balance, item.currency) }}</strong>
                  <small>{{ $t("billing.available_reward_credit_help") }}</small>
                </div>
                <div class="wallet-divider" aria-hidden="true"></div>
                <div class="wallet-metric">
                  <span>{{ $t("billing.pending_reward_commitments") }}</span>
                  <strong>{{ formatMoney(pendingFor(item.currency), item.currency) }}</strong>
                  <small>{{ $t("billing.pending_reward_commitments_help") }}</small>
                </div>
              </div>
            </article>
          </div>

          <div v-else-if="!billingLoadError" class="billing-empty-state">
            <i class="el-icon-document" aria-hidden="true"></i>
            <h3>{{ $t("billing.no_charges") }}</h3>
            <p>{{ $t("billing.no_charges_help") }}</p>
          </div>
        </section>

        <section class="billing-section" aria-labelledby="country-usage-heading">
          <div class="section-heading">
            <div>
              <div class="billing-eyebrow">{{ $t("billing.seat_evidence") }}</div>
              <h2 id="country-usage-heading">{{ $t("billing.member_usage_country") }}</h2>
            </div>
            <p>{{ $t("billing.country_usage_help") }}</p>
          </div>

          <div v-if="countryUsage.length" class="country-table-wrap">
            <table class="country-table">
              <thead>
                <tr>
                  <th scope="col">{{ $t("billing.Country") }}</th>
                  <th scope="col">{{ $t("billing.active_members") }}</th>
                  <th scope="col">{{ $t("billing.share_of_usage") }}</th>
                  <th scope="col">{{ $t("billing.last_activity") }}</th>
                  <th scope="col"><span class="sr-only">{{ $t("global.view") }}</span></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="country in countryUsage" :key="country.code">
                  <td>
                    <div class="country-name">
                      <span class="country-code">{{ country.code }}</span>
                      <strong>{{ country.name }}</strong>
                    </div>
                  </td>
                  <td>
                    <span class="mobile-label">{{ $t("billing.active_members") }}</span>
                    <strong>{{ addCommasInNumber(country.members, 0) }}</strong>
                  </td>
                  <td>
                    <span class="mobile-label">{{ $t("billing.share_of_usage") }}</span>
                    <div class="usage-share">
                      <div class="usage-track" aria-hidden="true">
                        <span :style="{ width: country.shareWidth + '%' }"></span>
                      </div>
                      <span>{{ country.shareLabel }}</span>
                    </div>
                  </td>
                  <td>
                    <span class="mobile-label">{{ $t("billing.last_activity") }}</span>
                    {{ country.lastActivity }}
                  </td>
                  <td class="country-action">
                    <el-button
                      type="text"
                      size="small"
                      class="billing-action-link"
                      @click="openCountryMembers(country.code)"
                    >
                      {{ $t("billing.view_members") }}
                    </el-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-else-if="!loadingDatableTable" class="billing-empty-state billing-empty-state--compact">
            <h3>{{ $t("billing.no_member_activity") }}</h3>
            <p>{{ $t("billing.no_member_activity_help") }}</p>
          </div>
        </section>

        <section class="billing-section" aria-labelledby="activity-heading">
          <div class="section-heading">
            <div>
              <div class="billing-eyebrow">{{ $t("billing.charge_details") }}</div>
              <h2 id="activity-heading">{{ $t("billing.billing_activity") }}</h2>
            </div>
            <p>{{ $t("billing.billing_activity_help") }}</p>
          </div>

          <div v-if="loadingDatableTable" class="ledger-skeleton" aria-label="Loading billing activity">
            <div v-for="item in 4" :key="'ledger-skeleton-' + item" class="skeleton-row"></div>
          </div>

          <div v-else-if="billingData.length" class="activity-groups">
            <article
              v-for="group in billingActivityGroups"
              :key="group.key"
              class="activity-group"
            >
              <div class="activity-group-heading">
                <div>
                  <h3>{{ group.title }}</h3>
                  <p>{{ group.help }}</p>
                </div>
                <span class="activity-count">
                  {{ activityEntryLabel(group.rows.length) }}
                </span>
              </div>

              <div class="ledger-table-wrap">
                <table class="ledger-table">
                  <thead>
                    <tr>
                      <th scope="col">{{ $t("billing.Date") }}</th>
                      <th scope="col">{{ $t("billing.Description") }}</th>
                      <th scope="col" class="amount-column">{{ $t("billing.Amount") }}</th>
                      <th scope="col">{{ $t("billing.Status") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in group.rows" :key="row.sortdate + '-' + index">
                      <td>
                        <span class="mobile-label">{{ $t("billing.Date") }}</span>
                        {{ row.table_date }}
                      </td>
                      <td>
                        <span class="mobile-label">{{ $t("billing.Description") }}</span>
                        <div class="transaction-description">
                          <strong>{{ activityDescription(row) }}</strong>
                          <el-button
                            v-if="row.table_category === 'Subscription'"
                            type="text"
                            size="small"
                            class="billing-action-link inline-member-link"
                            @click="showActiveUser(row)"
                          >
                            {{ addCommasInNumber(row.active_users, 0) }}
                            {{ $t("billing.active_members") }}
                            <span v-if="row.show_minimum">
                              · {{ addCommasInNumber(row.minimum_users, 0) }}
                              {{ $t("billing.seats_charged") }}
                            </span>
                          </el-button>
                          <small v-else-if="transactionDetail(row)">
                            {{ transactionDetail(row) }}
                          </small>
                        </div>
                      </td>
                      <td class="amount-column">
                        <span class="mobile-label">{{ $t("billing.Amount") }}</span>
                        <strong>{{ transactionAmount(row) }}</strong>
                      </td>
                      <td>
                        <span class="mobile-label">{{ $t("billing.Status") }}</span>
                        <span class="span-status-tag" :class="'tag-' + row.table_status">
                          {{ statusLabel(row.table_status) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </article>
          </div>

          <div v-else-if="!billingLoadError" class="billing-empty-state billing-empty-state--compact">
            <h3>{{ $t("billing.no_activity") }}</h3>
            <p>{{ $t("billing.no_activity_help") }}</p>
          </div>
        </section>
      </main>
    </div>

    <el-dialog
      :visible.sync="dialogActiveMembers"
      :title="memberDialogTitle"
      custom-class="billing-members-dialog"
      width="860px"
      top="7vh"
      @closed="activeModlaTab = 'all'"
    >
      <div class="member-dialog-summary">
        <div>
          <div class="billing-eyebrow">{{ selectedCountryName }}</div>
          <strong>
            {{ addCommasInNumber(selectedActiveMembers.length, 0) }}
            {{ $t("billing.active_members") }}
          </strong>
        </div>
        <div class="member-dialog-actions">
          <el-button
            type="text"
            class="header-icon-btn"
            icon="el-icon-document-copy"
            @click="copyTableData"
          >
            {{ $t("billing.copy_list") }}
          </el-button>
          <el-button
            type="text"
            class="header-icon-btn"
            icon="el-icon-download"
            @click="downloadMemberCsv"
          >
            {{ $t("billing.download_csv") }}
          </el-button>
        </div>
      </div>

      <el-tabs
        v-if="countryTabs.length > 1"
        v-model="activeModlaTab"
        class="billing-country-tabs"
      >
        <el-tab-pane name="all">
          <span slot="label">
            {{ $t("billing.all") }} ({{ addCommasInNumber(activeMember.length, 0) }})
          </span>
        </el-tab-pane>
        <el-tab-pane
          v-for="country in countryTabs"
          :key="country.code"
          :name="country.code"
        >
          <span slot="label">
            {{ country.name }} ({{ addCommasInNumber(country.members, 0) }})
          </span>
        </el-tab-pane>
      </el-tabs>

      <div class="members-recipients thin-scrollbar" :class="{ blurelement: blurMode }">
        <user-active-table :tableData="selectedActiveMembers"></user-active-table>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import format from "date-fns/format";

export default {
  name: "billing",
  components: {
    userActiveTable: require("./even/table/user-active").default,
  },
  data() {
    return {
      dialogActiveMembers: false,
      modal_monthofyear: "",
      modal_active: 0,
      activeModlaTab: "all",
      billingLoadError: "",
      lastUpdatedAt: null,
    };
  },
  created() {
    this.loadBillingPage();
    this.$store.commit("authen/setActiveIndex", "2");
  },
  mounted() {
    this.routeContainer = this.$el.closest(".components-container");
    if (this.routeContainer) {
      this.routeContainer.classList.add("billing-route-container");
    }
  },
  beforeDestroy() {
    if (this.routeContainer) {
      this.routeContainer.classList.remove("billing-route-container");
    }
  },
  computed: {
    user() {
      return this.$store.getters["authen/user"];
    },
    billingData() {
      return this.$store.getters["billing/billingData"] || [];
    },
    activeMember() {
      return this.$store.getters["billing/activeMember"] || [];
    },
    MonthOfYearOpt() {
      return this.$store.getters["billing/MonthOfYearOpt"] || [];
    },
    selectMonthOfYear() {
      return this.$store.getters["billing/selectMonthOfYear"] || {};
    },
    selectedMonthValue() {
      return this.selectMonthOfYear.value || "";
    },
    loadingDatableTable() {
      return this.$store.getters["billing/loadingDatableTable"];
    },
    companySymbol() {
      return this.$store.getters["billing/companySymbol"];
    },
    allSymbol() {
      return this.$store.getters["billing/allSymbol"] || {};
    },
    countryInCompany() {
      const countries = this.$store.getters["billing/countryInCompany"];
      return Array.isArray(countries) ? countries : [];
    },
    countryMember() {
      return this.$store.getters["billing/countryMember"] || {};
    },
    billingSummary() {
      return this.$store.getters["billing/billingSummary"] || [];
    },
    billingPending() {
      return this.$store.getters["billing/billingPending"] || {};
    },
    billingActivityGroups() {
      const isSubscription = (row) => row.table_category === "Subscription";
      const isTopup = (row) => row.table_description === "Top-up Credits";
      const isRedemption = (row) =>
        row.table_category === "Rewards" && !isTopup(row);
      const groups = [
        {
          key: "subscription",
          title: this.$t("billing.subscription_charges"),
          help: this.$t("billing.subscription_charges_help"),
          rows: this.billingData.filter(isSubscription),
        },
        {
          key: "topups",
          title: this.$t("billing.reward_credit_topups"),
          help: this.$t("billing.reward_credit_topups_help"),
          rows: this.billingData.filter(isTopup),
        },
        {
          key: "redemptions",
          title: this.$t("billing.reward_redemptions"),
          help: this.$t("billing.reward_redemptions_help"),
          rows: this.billingData.filter(isRedemption),
        },
        {
          key: "other",
          title: this.$t("billing.other_billing_activity"),
          help: this.$t("billing.other_billing_activity_help"),
          rows: this.billingData.filter(
            (row) =>
              !isSubscription(row) && !isTopup(row) && !isRedemption(row)
          ),
        },
      ];
      return groups.filter((group) => group.rows.length);
    },
    htmlExport() {
      return this.$store.getters["billing/htmlExport"];
    },
    blurMode() {
      return this.$store.getters["demomode/blurMode"];
    },
    subscriptionRow() {
      return this.billingData.find((row) => row.table_category === "Subscription") || null;
    },
    periodLabel() {
      if (this.selectMonthOfYear.label) return this.selectMonthOfYear.label;
      if (this.subscriptionRow && this.subscriptionRow.table_date) {
        return format(new Date(this.subscriptionRow.table_date), "MMMM YYYY");
      }
      return this.$t("billing.current_period");
    },
    billingPeriodDescription() {
      if (this.subscriptionRow && this.subscriptionRow.table_description) {
        return this.subscriptionRow.table_description;
      }
      return this.$t("billing.period_range_pending");
    },
    periodStatus() {
      if (this.loadingDatableTable) {
        return { label: this.$t("billing.calculating"), tone: "loading" };
      }
      const status = this.subscriptionRow && this.subscriptionRow.table_status;
      if (status === "bill-sent" || status === "paid" || status === "charged") {
        return { label: this.$t("billing.finalised"), tone: "final" };
      }
      return { label: this.$t("billing.in_progress"), tone: "pending" };
    },
    lastUpdatedLabel() {
      if (!this.lastUpdatedAt) return this.$t("billing.waiting_for_activity");
      return this.$t("billing.updated_at") + " " + format(this.lastUpdatedAt, "MMM D, YYYY h:mm A");
    },
    countryTabs() {
      return this.countryUsage;
    },
    countryUsage() {
      const names = {};
      this.countryInCompany.forEach((country) => {
        names[country.id] = country.country;
      });
      const total = this.activeMember.length;
      return Object.keys(this.countryMember)
        .map((code) => {
          const members = Array.isArray(this.countryMember[code])
            ? this.countryMember[code]
            : [];
          const shareValue = total ? (members.length / total) * 100 : 0;
          return {
            code,
            name: names[code] || code,
            members: members.length,
            shareWidth: shareValue > 0 ? Math.max(shareValue, 1.5) : 0,
            shareLabel:
              shareValue > 0 && shareValue < 1
                ? this.$t("billing.less_than_one_percent")
                : Math.round(shareValue) + "%",
            lastActivity: this.latestActivity(members),
          };
        })
        .filter((country) => country.members > 0)
        .sort((a, b) => b.members - a.members);
    },
    selectedActiveMembers() {
      if (this.activeModlaTab === "all") return this.activeMember;
      const members = this.countryMember[this.activeModlaTab];
      return Array.isArray(members) ? members : [];
    },
    selectedCountryName() {
      if (this.activeModlaTab === "all") return this.$t("billing.all_countries");
      const country = this.countryUsage.find((item) => item.code === this.activeModlaTab);
      return country ? country.name : this.activeModlaTab;
    },
    memberDialogTitle() {
      return this.periodLabel + " · " + this.$t("billing.billable_member_activity");
    },
  },
  methods: {
    async loadBillingPage() {
      this.billingLoadError = "";
      try {
        await this.$store.dispatch("billing/getMasterRewards", this.user);
        const periodOptionsPromise = this.$store
          .dispatch("billing/getMonthOfYearOpt", this.user)
          .catch(() => null);
        await this.$store.dispatch("billing/getBillingData", this.user);
        this.lastUpdatedAt = new Date();
        await periodOptionsPromise;
      } catch (error) {
        this.$store.commit("billing/setLoading", false);
        this.billingLoadError =
          (error && error.message) || this.$t("billing.load_error_help");
      }
    },
    async monthBilling(value) {
      const month = this.MonthOfYearOpt.find((item) => item.value === value);
      if (!month) return;
      this.billingLoadError = "";
      this.$store.commit("billing/setSelectMonthOfYear", month);
      try {
        await this.$store.dispatch("billing/getBillingData", this.user);
        this.lastUpdatedAt = new Date();
      } catch (error) {
        this.$store.commit("billing/setLoading", false);
        this.billingLoadError =
          (error && error.message) || this.$t("billing.load_error_help");
      }
    },
    safeNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    },
    addCommasInNumber(value, decimals = 2) {
      return this.safeNumber(value).toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
    },
    currencySymbol(currency) {
      return this.allSymbol[currency] || currency || "";
    },
    formatMoney(value, currency, symbolOverride) {
      const symbol = symbolOverride || this.currencySymbol(currency);
      return symbol + this.addCommasInNumber(value, 2);
    },
    summaryTotal(item) {
      return (
        this.safeNumber(item.subscription) +
        this.safeNumber(item.reward_charges) +
        this.safeNumber(item.topupCredit)
      );
    },
    pendingFor(currency) {
      return this.billingPending[currency]
        ? this.safeNumber(this.billingPending[currency].reward_pending)
        : 0;
    },
    latestActivity(members) {
      const timestamps = members
        .map((member) => {
          const date = member.lastDate || "";
          const time = member.time && member.time !== "12:00 AM" ? " " + member.time : "";
          return new Date(date + time).getTime();
        })
        .filter((timestamp) => Number.isFinite(timestamp));
      if (!timestamps.length) return this.$t("billing.not_available");
      return format(new Date(Math.max.apply(null, timestamps)), "MMM D, YYYY");
    },
    transactionDetail(row) {
      if (row.table_description === "Top-up Credits") {
        if (row.balance === undefined || row.balance === null) return "";
        return (
          this.$t("billing.credit_balance") +
          ": " +
          this.formatMoney(row.balance, row.credit_currency)
        );
      }
      return row.detail_txt || "";
    },
    activityDescription(row) {
      if (row.table_category === "Subscription") {
        return this.$t("billing.member_subscription");
      }
      if (row.table_description === "Top-up Credits") {
        return this.$t("billing.reward_credit_topup");
      }
      const rewardName = String(row.table_description || "").replace(
        /^Redeemed\s*-\s*/i,
        ""
      );
      return rewardName
        ? this.$t("billing.reward_redemption") + " · " + rewardName
        : this.$t("billing.reward_redemption");
    },
    activityEntryLabel(count) {
      return (
        this.addCommasInNumber(count, 0) +
        " " +
        this.$t(count === 1 ? "billing.entry" : "billing.entries")
      );
    },
    transactionAmount(row) {
      const hasAmount = row.total !== undefined && row.total !== null;
      if (!hasAmount) return this.$t("billing.not_available");
      const currency =
        row.credit_currency ||
        row.reward_currency ||
        this.companySymbol;
      return this.formatMoney(row.total, currency, row.symbol);
    },
    statusLabel(status) {
      if (status === "bill-sent") return this.$t("billing.bill_sent");
      if (status === "pending") return this.$t("billing.pending");
      if (status === "charged") return this.$t("billing.charged");
      if (status === "paid") return this.$t("billing.paid");
      if (status === "canceled" || status === "cancelled") {
        return this.$t("billing.canceled");
      }
      return status || this.$t("billing.not_available");
    },
    showActiveUser(item) {
      this.modal_monthofyear = this.periodLabel;
      this.modal_active = item.active_users || this.activeMember.length;
      this.activeModlaTab = "all";
      this.dialogActiveMembers = true;
    },
    openCountryMembers(countryCode) {
      this.modal_monthofyear = this.periodLabel;
      this.activeModlaTab = countryCode;
      this.modal_active = this.selectedActiveMembers.length;
      this.dialogActiveMembers = true;
    },
    exportHtml() {
      if (this.loadingDatableTable || !this.htmlExport) return;
      const newTab = window.open("", "_blank");
      if (!newTab) {
        this.$message({
          type: "warning",
          message: this.$t("billing.allow_popups"),
          duration: 4000,
        });
        return;
      }
      newTab.document.write(this.htmlExport);
      newTab.document.close();
    },
    memberRowsForExport() {
      return this.selectedActiveMembers.map((member) => [
        member.firstname || member.first_name || "",
        member.lastname || member.last_name || "",
        member.email || "",
        member.team_name || "",
        member.lastDate || member.lastdate || member.last_date || "",
        member.time && member.time !== "12:00 AM" ? member.time : "",
      ]);
    },
    copyTableData() {
      if (!this.selectedActiveMembers.length) {
        this.$message({
          message: this.$t("billing.no_data_to_copy"),
          type: "warning",
          duration: 2000,
        });
        return;
      }
      const headers = [
        "First Name",
        "Last Name",
        "Email Address",
        "Team Name",
        "Last Active Date",
        "Last Active Time",
      ];
      const textToCopy = [
        this.periodLabel,
        this.selectedCountryName,
        this.selectedActiveMembers.length + " active members",
        "",
        headers.join("\t"),
        ...this.memberRowsForExport().map((row) => row.join("\t")),
      ].join("\n");
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard
          .writeText(textToCopy)
          .then(() => {
            this.$message({
              message: this.$t("billing.copied_to_clipboard"),
              type: "success",
              duration: 2000,
            });
          })
          .catch(() => this.fallbackCopyToClipboard(textToCopy));
      } else {
        this.fallbackCopyToClipboard(textToCopy);
      }
    },
    fallbackCopyToClipboard(text) {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        this.$message({
          message: this.$t("billing.copied_to_clipboard"),
          type: "success",
          duration: 2000,
        });
      } catch (error) {
        this.$message({
          message: this.$t("billing.failed_to_copy"),
          type: "error",
          duration: 2000,
        });
      }
    },
    csvCell(value) {
      return '"' + String(value === undefined || value === null ? "" : value).replace(/"/g, '""') + '"';
    },
    downloadMemberCsv() {
      if (!this.selectedActiveMembers.length) {
        this.$message({
          message: this.$t("billing.no_data_to_copy"),
          type: "warning",
          duration: 2000,
        });
        return;
      }
      const headers = [
        "First Name",
        "Last Name",
        "Email Address",
        "Team Name",
        "Last Active Date",
        "Last Active Time",
      ];
      const rows = [headers, ...this.memberRowsForExport()]
        .map((row) => row.map(this.csvCell).join(","))
        .join("\n");
      const blob = new Blob(["\uFEFF" + rows], { type: "text/csv;charset=utf-8" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download =
        "happily-active-members-" +
        this.selectedMonthValue +
        "-" +
        this.activeModlaTab +
        ".csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    },
  },
};
</script>

<style scoped lang="scss">
#billingContainer {
  color: #2f2f2f;
}

.billing-page-heading {
  min-width: 0;
}

.billing-page-description {
  min-width: 0;
}

.billing-workspace {
  margin-top: 32px;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 18px;
}

.period-panel,
.billing-section {
  min-width: 0;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #e2e4ea;
  border-radius: 12px;
}

.period-panel {
  display: flex;
  justify-content: space-between;
  gap: 32px;
  padding: 24px;
}

.billing-eyebrow {
  color: #6c30cc;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1.3;
  text-transform: uppercase;
}

.period-title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;

  h2 {
    margin: 0;
    font-size: 30px;
    font-weight: 800;
    letter-spacing: -0.01em;
    line-height: 1.2;
  }
}

.period-status {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
}

.period-status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}

.period-status--pending {
  color: #755510;
  background: #fff4d5;
}

.period-status--final {
  color: #157244;
  background: #e9f8f0;
}

.period-status--loading {
  color: #5b36a0;
  background: #f0e9fb;
}

.period-range {
  margin: 10px 0 0;
  color: #4a4a4a;
  font-size: 14px;
  font-weight: 700;
}

.period-updated {
  margin: 5px 0 0;
  color: #6b7280;
  font-size: 12px;
}

.period-actions {
  display: grid;
  grid-template-columns: minmax(190px, 220px) auto;
  align-content: center;
  align-items: end;
  gap: 8px 12px;
  min-width: 360px;
}

.period-select-label {
  grid-column: 1 / -1;
  color: #6b7280;
  font-size: 12px;
  font-weight: 800;
}

.period-select {
  width: 100%;
}

.calculation-note {
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 11px 15px;
  color: #6b7280;
  background: #f6f7f9;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;

  i {
    margin-top: 2px;
    color: #6c30cc;
  }
}

.billing-section {
  padding: 24px;
}

.section-heading {
  display: flex;
  justify-content: space-between;
  gap: 30px;
  align-items: flex-end;
  margin-bottom: 22px;

  h2 {
    margin: 7px 0 0;
    color: #2f2f2f;
    font-size: 18px;
    font-weight: 700;
    line-height: 1.25;
  }

  p {
    max-width: 430px;
    margin: 0;
    color: #6b7280;
    font-size: 13px;
    line-height: 1.5;
    text-align: right;
  }
}

.currency-summary-list {
  display: grid;
  gap: 18px;
}

.currency-summary {
  border: 1px solid #e2e4ea;
  border-radius: 8px;
  overflow: hidden;
}

.currency-summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  background: #fafbfc;
  border-bottom: 1px solid #e2e4ea;
}

.currency-code {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  height: 26px;
  padding: 0 8px;
  color: #6c30cc;
  background: rgba(108, 48, 204, 0.08);
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.currency-summary-label {
  margin-left: 10px;
  color: #56566a;
  font-size: 13px;
  font-weight: 800;
}

.currency-total {
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.billing-equation {
  display: grid;
  grid-template-columns: minmax(120px, 1fr) auto minmax(120px, 1fr) auto minmax(120px, 1fr) auto minmax(135px, 1.1fr);
  gap: 14px;
  align-items: center;
  padding: 22px 20px;
}

.equation-item {
  min-width: 0;

  span {
    display: block;
    min-height: 32px;
    color: #6b7280;
    font-size: 11px;
    font-weight: 800;
    line-height: 1.35;
    text-transform: uppercase;
  }

  strong {
    display: block;
    margin-top: 6px;
    color: #2f2f2f;
    font-size: 18px;
    line-height: 1.2;
  }
}

.equation-item--total {
  padding: 13px 15px;
  background: rgba(108, 48, 204, 0.08);
  border: 1px solid rgba(108, 48, 204, 0.25);
  border-radius: 8px;

  span,
  strong {
    color: #6c30cc;
  }

  span {
    min-height: auto;
    opacity: 1;
  }

  strong {
    font-size: 20px;
  }
}

.equation-operator {
  color: #aaa4b6;
  font-size: 19px;
  font-weight: 700;
}

.equation-operator--equals {
  color: #6c30cc;
}

.wallet-strip {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 22px;
  padding: 18px 20px;
  background: #f6f7f9;
  border-top: 1px solid #e2e4ea;
}

.wallet-metric {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 4px 14px;

  > span {
    color: #59566a;
    font-size: 12px;
    font-weight: 800;
  }

  strong {
    color: #2b2437;
    font-size: 17px;
  }

  small {
    grid-column: 1 / -1;
    color: #858095;
    font-size: 11px;
    line-height: 1.4;
  }
}

.wallet-divider {
  width: 1px;
  background: #ddd6e8;
}

.country-table-wrap,
.ledger-table-wrap {
  overflow-x: auto;
  border: 1px solid #e2e4ea;
  border-radius: 8px;
}

.activity-groups {
  display: grid;
  gap: 22px;
}

.activity-group {
  min-width: 0;
}

.activity-group-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 10px;

  h3 {
    margin: 0;
    color: #2f2f2f;
    font-size: 15px;
    line-height: 1.35;
  }

  p {
    max-width: 680px;
    margin: 3px 0 0;
    color: #6b7280;
    font-size: 12px;
    line-height: 1.45;
  }
}

.activity-count {
  flex: 0 0 auto;
  padding: 5px 9px;
  color: #6b7280;
  background: #f6f7f9;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
}

.country-table,
.ledger-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    padding: 13px 16px;
    color: #6b7280;
    background: #fafbfc;
    border-bottom: 1px solid #f0f1f5;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-align: left;
    text-transform: uppercase;
  }

  td {
    padding: 15px 16px;
    color: #2f2f2f;
    border-bottom: 1px solid #f0f1f5;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.country-name {
  display: flex;
  align-items: center;
  gap: 10px;
}

.country-code {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 26px;
  color: #6c30cc;
  background: rgba(108, 48, 204, 0.08);
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
}

.usage-share {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 150px;
}

.usage-track {
  width: 96px;
  height: 6px;
  overflow: hidden;
  background: #ece8f2;
  border-radius: 999px;

  span {
    display: block;
    height: 100%;
    background: #6c30cc;
    border-radius: inherit;
  }
}

.country-action {
  text-align: right;
}

.billing-action-link.el-button--text {
  color: #6c30cc;
  font-weight: 600;

  &:hover,
  &:focus {
    color: #5724aa;
  }

  &:focus-visible {
    outline: 2px solid rgba(108, 48, 204, 0.3);
    outline-offset: 2px;
  }
}

.ledger-table {
  min-width: 680px;

  th:nth-child(1) { width: 18%; }
  th:nth-child(2) { width: 48%; }
  th:nth-child(3) { width: 18%; }
  th:nth-child(4) { width: 16%; }
}

.amount-column {
  text-align: right !important;
  white-space: nowrap;
}

.transaction-description {
  display: grid;
  gap: 5px;

  strong {
    font-weight: 700;
  }

  small {
    color: #858095;
    line-height: 1.4;
  }
}

.inline-member-link {
  width: fit-content;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
}

.billing-alert {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  align-items: center;
  padding: 16px 18px;
  color: #8d2734;
  background: #fff2f3;
  border: 1px solid #f2c5ca;
  border-radius: 10px;

  p {
    margin: 4px 0 0;
    font-size: 13px;
  }
}

.billing-empty-state {
  padding: 40px 24px;
  color: #777386;
  text-align: center;

  i {
    color: #b8adc9;
    font-size: 30px;
  }

  h3 {
    margin: 12px 0 5px;
    color: #343143;
    font-size: 16px;
  }

  p {
    margin: 0;
    font-size: 13px;
  }
}

.billing-empty-state--compact {
  padding: 26px 18px;
  border: 1px dashed #ddd7e6;
  border-radius: 10px;
}

.summary-skeleton {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

.skeleton-block,
.skeleton-row {
  background: linear-gradient(90deg, #f2eff6 25%, #faf9fc 50%, #f2eff6 75%);
  background-size: 200% 100%;
  border-radius: 9px;
  animation: billing-shimmer 1.5s ease-in-out infinite;
}

.skeleton-block {
  height: 150px;
}

.ledger-skeleton {
  display: grid;
  gap: 9px;
}

.skeleton-row {
  height: 54px;
}

.mobile-label {
  display: none;
}

.member-dialog-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin: -4px 0 14px;

  strong {
    display: block;
    margin-top: 5px;
    color: #2f2f2f;
    font-size: 18px;
  }
}

.member-dialog-actions {
  display: flex;
  gap: 8px;

  .el-button + .el-button {
    margin-left: 0;
  }
}

.billing-country-tabs {
  margin-top: 8px;
}

.members-recipients {
  max-height: 56vh;
  overflow: auto;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@keyframes billing-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton-block,
  .skeleton-row {
    animation: none;
  }
}

@media (max-width: 1080px) {
  .billing-equation {
    grid-template-columns: 1fr auto 1fr auto 1fr;
  }

  .equation-operator--equals {
    display: none;
  }

  .equation-item--total {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
}

@media (max-width: 820px) {
  .billing-workspace {
    margin-top: 24px;
  }

  .period-panel,
  .section-heading,
  .member-dialog-summary {
    align-items: stretch;
    flex-direction: column;
  }

  .period-actions {
    min-width: 0;
  }

  .section-heading p {
    max-width: none;
    text-align: left;
  }

  .billing-section,
  .period-panel {
    padding: 22px 20px;
  }

  .billing-equation {
    grid-template-columns: 1fr auto 1fr;
  }

  .billing-equation .equation-item:nth-of-type(3) {
    grid-column: 1 / 2;
  }

  .wallet-strip {
    grid-template-columns: 1fr;
  }

  .wallet-divider {
    width: 100%;
    height: 1px;
  }

  .member-dialog-actions {
    flex-wrap: wrap;
  }
}

@media (max-width: 640px) {
  .billing-page-heading {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .billing-page-description {
    flex-basis: 100%;
    margin-top: 8px;
    margin-left: 64px;
  }

  .period-title-row {
    align-items: flex-start;
    flex-direction: column;
  }

  .period-actions {
    grid-template-columns: 1fr;
  }

  .period-select-label {
    grid-column: auto;
  }

  .billing-export-button {
    width: 100%;
  }

  .currency-summary-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .billing-equation {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .equation-operator {
    display: none;
  }

  .equation-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    padding: 9px 0;
    border-bottom: 1px solid #efedf3;

    span {
      min-height: auto;
    }

    strong {
      margin-top: 0;
      white-space: nowrap;
    }
  }

  .equation-item--total {
    grid-column: auto;
    border-bottom: none;
  }

  .country-table-wrap,
  .ledger-table-wrap {
    overflow: visible;
    border: none;
  }

  .activity-group-heading {
    align-items: flex-start;

    p {
      max-width: none;
    }
  }

  .country-table,
  .ledger-table {
    min-width: 0;

    thead {
      display: none;
    }

    tbody,
    tr,
    td {
      display: block;
      width: 100%;
      box-sizing: border-box;
    }

    tr {
      margin-bottom: 12px;
      padding: 14px 15px;
      border: 1px solid #e8e4ee;
      border-radius: 10px;
    }

    td {
      display: grid;
      grid-template-columns: 112px minmax(0, 1fr);
      gap: 12px;
      padding: 8px 0;
      border-bottom: 1px solid #f0edf3;
      text-align: left !important;
    }

    td:last-child {
      border-bottom: none;
    }
  }

  .country-name {
    grid-column: 1 / -1;
  }

  .country-action {
    display: block !important;
  }

  .mobile-label {
    display: inline;
    color: #7c7888;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .usage-share {
    min-width: 0;
  }

  .usage-track {
    width: min(90px, 70%);
  }

  .summary-skeleton {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="scss">
.components-container.billing-route-container {
  min-width: 0 !important;
}

.billing-route-container {
  .page-container {
    width: 100%;
  }

  .page-content {
    width: min(992px, calc(100% - 32px));
    box-sizing: border-box;
  }
}

@media (max-width: 1024px) {
  .components-container.billing-route-container {
    width: 100vw !important;
  }
}

#billingContainer {
  .period-select .el-input__inner {
    height: 40px;
    border-color: #e2e4ea;
    border-radius: 8px;
    color: #2f2f2f;
    font-weight: 600;
  }

  .billing-members-dialog {
    max-width: calc(100vw - 32px);
    border-radius: 12px;
    overflow: hidden;

    .el-dialog__header {
      padding: 22px 26px 14px;
      border-bottom: 1px solid #ece8f1;
    }

    .el-dialog__title {
      color: #2f2f2f;
      font-size: 18px;
      font-weight: 700;
    }

    .el-dialog__body {
      padding: 22px 26px 26px;
    }
  }

  .billing-country-tabs {
    .el-tabs__header {
      margin-bottom: 10px;
    }

    .el-tabs__item {
      height: 42px;
      color: #6a6676;
      font-size: 13px;
    }

    .el-tabs__item.is-active {
      color: #6c30cc;
      font-weight: 800;
    }

    .el-tabs__active-bar {
      background-color: #6c30cc;
    }
  }
}
</style>
